import { getGroupText } from '@/utilities/order.v2';
import { es6MapMap, groupBy, mapEntries } from '@larki/shared/object';
import React, { Fragment, useMemo } from 'react';
import WhiteContainer from '../../../components/containers/WhiteContainer';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { orderV2Selectors } from '../../../redux/selectors/order.v2';
import text, { formatArea } from '../../../text';
import { usePaymentContext } from '../../hooks/usePaymentContext';
import GrandTotal from './GrandTotal';
import { InvoiceTotal } from './InvoiceTotal';

const ForQuotation: React.FC = () => {
  const { jobId, formatPrice, asMember } = usePaymentContext();
  const quote = useAppSelector((state) =>
    orderV2Selectors.getQuote(state, jobId, asMember)
  );
  const hasNonRfqCharges = useAppSelector(
    (state) =>
      !!orderV2Selectors.getNonRfqCharges(state, jobId, asMember)?.length
  );
  const rfqCharges = useAppSelector((state) =>
    orderV2Selectors.getRfqCharges(state, jobId, asMember)
  );
  const hasRfqCharges = useAppSelector(
    (state) => !!orderV2Selectors.getRfqCharges(state, jobId, asMember)?.length
  );

  const overriddenSet = useAppSelector((state) =>
    orderV2Selectors.getOverriddenSet(state, jobId, asMember)
  );

  const customOrders = useMemo(() => {
    const all = quote?.customOrders ?? [];
    if (overriddenSet.size === 0) return all;
    return all.filter((o) => !overriddenSet.has(`custom_${o.id}`));
  }, [quote?.customOrders, overriddenSet]);
  const hasCustomOrders = customOrders.length > 0;

  const nonRfqCompositionLength =
    Object.values(quote?.nonRfq.composition || {}).length || 0;

  const overriddenRfqGroupCount = useMemo(() => {
    if (!rfqCharges || overriddenSet.size === 0) return 0;
    const overridden = rfqCharges.filter((c) => overriddenSet.has(c.product));
    if (overridden.length === 0) return 0;
    return new Set(overridden.map((c) => c.group)).size;
  }, [rfqCharges, overriddenSet]);

  const overriddenCustomOrderCount = useMemo(() => {
    if (overriddenSet.size === 0) return 0;
    return (quote?.customOrders ?? []).filter((o) =>
      overriddenSet.has(`custom_${o.id}`)
    ).length;
  }, [quote?.customOrders, overriddenSet]);

  const buyNowGroupCount =
    nonRfqCompositionLength +
    overriddenRfqGroupCount +
    (overriddenCustomOrderCount > 0 ? 1 : 0);

  const captureServicesFees = useMemo(() => {
    if (overriddenSet.has('site_deployment_fee')) return [];
    return (quote?.rfq.composition.capture_services?.totals.fees ?? []).map(
      (fee) => ({
        product: text(fee.metadata.id),
        subtotal: fee.amount,
      })
    );
  }, [quote, overriddenSet]);

  const rfqChargeGroups = useMemo(() => {
    if (!rfqCharges) return null;
    const remaining =
      overriddenSet.size > 0
        ? rfqCharges.filter((c) => !overriddenSet.has(c.product))
        : rfqCharges;

    const byGroup = groupBy(remaining, 'group');

    // Fee-only GET QUOTE row: all capture_services charges overridden, but the fee remains here.
    if (!byGroup.has('capture_services') && captureServicesFees.length > 0) {
      byGroup.set('capture_services', []);
    }

    if (byGroup.size === 0) return null;

    return es6MapMap((charges, groupName) => {
      const isCapture = groupName === 'capture_services';
      const _charges = charges.map((charge) => {
        const productName = `${text(`${charge.product}3D`)} (${formatArea(charge.quantity)}²)`;
        return {
          product: productName,
          subtotal: charge.subtotal,
        };
      });

      if (isCapture) {
        return [..._charges, ...captureServicesFees];
      }

      return _charges;
    }, byGroup);
  }, [rfqCharges, quote, overriddenSet, captureServicesFees]);

  if (!quote) return null;

  const hasRemainingRfq = (rfqChargeGroups?.size ?? 0) > 0;
  if (!hasRemainingRfq && !hasCustomOrders) {
    return null;
  }

  return (
    <div className='rfq'>
      {hasNonRfqCharges || overriddenSet.size > 0 ? (
        <hr className='quote-divider' />
      ) : null}
      <div className='d-flex flex-row align-align-items-center mb-3 get-quote-section justify-content-between'>
        <p className='text-uppercase font-16 grey-3 font-weight--600 mb-0'>
          {text('getQuote')}:
        </p>
      </div>

      <div>
        <p className='rfq-disclaimer'>{text('requestForQuoteDisclaimer')}</p>
      </div>

      <WhiteContainer className='Invoice px-3 py-2'>
        {rfqChargeGroups
          ? mapEntries(rfqChargeGroups, (charges, group, index) => (
              <Fragment key={group}>
                <div className='d-flex flex-row justify-content-between align-items-center'>
                  <h6 className='mb-0'>
                    <span className='mr-1'>
                      {index + 1 + buyNowGroupCount}.
                    </span>{' '}
                    <span>{getGroupText(group)}</span>
                  </h6>
                </div>
                <div
                  className='d-flex flex-column flex-gap pl-4 pb-2'
                  style={{ paddingRight: 16 }}
                >
                  {charges.map((charge) => (
                    <div
                      key={charge.product}
                      className='d-flex flex-row justify-content-between align-items-center font-grey--3'
                    >
                      <span>{charge.product}</span>
                      <span>
                        {charge.subtotal === 'TBC'
                          ? text('toBeConfirmed')
                          : `~${formatPrice(charge.subtotal)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </Fragment>
            ))
          : null}
        {hasCustomOrders && (
          <Fragment>
            <div className='d-flex flex-row justify-content-between align-items-center'>
              <h6 className='mb-0'>
                <span className='mr-1'>
                  {(rfqChargeGroups?.size ?? 0) + 1 + buyNowGroupCount}.
                </span>{' '}
                <span>{text('customOrder')}s</span>
              </h6>
            </div>
            <div
              className='d-flex flex-column pl-4 flex-gap pb-2'
              style={{ paddingRight: 16 }}
            >
              {customOrders.map((order) => (
                <div
                  key={order.id}
                  className='d-flex flex-row justify-content-between align-items-center font-grey--3'
                >
                  <span>{order.name}</span>
                  <span>
                    {order.price === 'TBC'
                      ? text('toBeConfirmed')
                      : `~${formatPrice(order.price)}`}
                  </span>
                </div>
              ))}
            </div>
          </Fragment>
        )}
      </WhiteContainer>

      <div className='mt-2'>
        <InvoiceTotal isRfq={true} isApproximate={true} />
      </div>

      {(hasNonRfqCharges || overriddenSet.size > 0) && (
        <hr className='quote-divider my-4' />
      )}

      {(hasNonRfqCharges || overriddenSet.size > 0) &&
      (hasRfqCharges || hasCustomOrders) ? (
        <GrandTotal isApproximate={true} />
      ) : null}

      <div>
        <p className='rfq-contact-us'>
          {text('requestForQuoteContactUs', {
            contact: (
              <a
                className='d-inline'
                href={`${process.env.LARKI_MARKETING_URL}/contact-us`}
                target='_blank'
                rel='noreferrer'
              >
                contact LARKI
              </a>
            ),
          })}
        </p>
      </div>
    </div>
  );
};

export default ForQuotation;
